import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Loader2, Search, X, ArrowLeft, Users, MapPin,
  Building2, Droplet, Scale, Shield, GraduationCap, Zap,
  Factory, Landmark, Briefcase, Fuel, HardHat, Radio,
  TreePine, Building, Truck, Camera, PenLine, FileCheck, FileText,
} from "lucide-react";

/**
 * Infer icon + tint for a contract label. Mirrors the backend
 * _infer_contract rules so the visuals stay consistent.
 */
function pickContractStyle(contract) {
  const rules = {
    "EMBASA":            { Icon: Droplet,       tint: "sky" },
    "PF":                { Icon: Shield,        tint: "slate" },
    "RECEITA FEDERAL":   { Icon: Landmark,      tint: "emerald" },
    "JUSTIÇA FEDERAL":   { Icon: Scale,         tint: "indigo" },
    "MPT":               { Icon: Scale,         tint: "purple" },
    "AGU":               { Icon: Scale,         tint: "amber" },
    "DSEI":              { Icon: TreePine,      tint: "green" },
    "FUNAI":             { Icon: TreePine,      tint: "orange" },
    "ENGIE":             { Icon: Zap,           tint: "yellow" },
    "UFSB":              { Icon: GraduationCap, tint: "rose" },
    "UNIVASF":           { Icon: GraduationCap, tint: "rose" },
    "IF BAIANO":         { Icon: GraduationCap, tint: "rose" },
    "JOVEM APRENDIZ":    { Icon: GraduationCap, tint: "pink" },
    "SERPRO":            { Icon: Radio,         tint: "cyan" },
    "CTI":               { Icon: Radio,         tint: "teal" },
    "ULTRACARGO":        { Icon: Fuel,          tint: "red" },
    "IRON":              { Icon: HardHat,       tint: "zinc" },
    "RMB":               { Icon: Truck,         tint: "stone" },
    "CONSÓRCIO":         { Icon: Factory,       tint: "amber" },
    "AVI":               { Icon: Building2,     tint: "blue" },
    "FILIAL":            { Icon: Building2,     tint: "blue" },
    "ASSUFBA":           { Icon: Building,      tint: "violet" },
    "ITEC":              { Icon: Building,      tint: "fuchsia" },
    "BOCÃO NEWS":        { Icon: Radio,         tint: "orange" },
  };
  return rules[contract] || { Icon: Briefcase, tint: "slate" };
}

const TINT_CLASSES = {
  sky:      { bg: "bg-sky-100",      text: "text-sky-700",      ring: "hover:ring-sky-300",      accent: "bg-sky-500" },
  slate:    { bg: "bg-slate-200",    text: "text-slate-700",    ring: "hover:ring-slate-300",    accent: "bg-slate-500" },
  emerald:  { bg: "bg-emerald-100",  text: "text-emerald-700",  ring: "hover:ring-emerald-300",  accent: "bg-emerald-500" },
  indigo:   { bg: "bg-indigo-100",   text: "text-indigo-700",   ring: "hover:ring-indigo-300",   accent: "bg-indigo-500" },
  purple:   { bg: "bg-purple-100",   text: "text-purple-700",   ring: "hover:ring-purple-300",   accent: "bg-purple-500" },
  amber:    { bg: "bg-amber-100",    text: "text-amber-700",    ring: "hover:ring-amber-300",    accent: "bg-amber-500" },
  green:    { bg: "bg-green-100",    text: "text-green-700",    ring: "hover:ring-green-300",    accent: "bg-green-500" },
  orange:   { bg: "bg-orange-100",   text: "text-orange-700",   ring: "hover:ring-orange-300",   accent: "bg-orange-500" },
  yellow:   { bg: "bg-yellow-100",   text: "text-yellow-700",   ring: "hover:ring-yellow-300",   accent: "bg-yellow-500" },
  rose:     { bg: "bg-rose-100",     text: "text-rose-700",     ring: "hover:ring-rose-300",     accent: "bg-rose-500" },
  pink:     { bg: "bg-pink-100",     text: "text-pink-700",     ring: "hover:ring-pink-300",     accent: "bg-pink-500" },
  cyan:     { bg: "bg-cyan-100",     text: "text-cyan-700",     ring: "hover:ring-cyan-300",     accent: "bg-cyan-500" },
  teal:     { bg: "bg-teal-100",     text: "text-teal-700",     ring: "hover:ring-teal-300",     accent: "bg-teal-500" },
  red:      { bg: "bg-red-100",      text: "text-red-700",      ring: "hover:ring-red-300",      accent: "bg-red-500" },
  zinc:     { bg: "bg-zinc-200",     text: "text-zinc-700",     ring: "hover:ring-zinc-300",     accent: "bg-zinc-500" },
  stone:    { bg: "bg-stone-200",    text: "text-stone-700",    ring: "hover:ring-stone-300",    accent: "bg-stone-500" },
  blue:     { bg: "bg-blue-100",     text: "text-blue-700",     ring: "hover:ring-blue-300",     accent: "bg-blue-500" },
  violet:   { bg: "bg-violet-100",   text: "text-violet-700",   ring: "hover:ring-violet-300",   accent: "bg-violet-500" },
  fuchsia:  { bg: "bg-fuchsia-100",  text: "text-fuchsia-700",  ring: "hover:ring-fuchsia-300",  accent: "bg-fuchsia-500" },
};

/**
 * AdminContratosView — three-level drill-down:
 *   Contratos → Postos do contrato → Funcionários do posto.
 */
export default function AdminContratosView({ onBack }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedPosto, setSelectedPosto] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API}/admin/contratos`, { timeout: 20000 });
        if (!cancelled) setContracts(r.data?.contracts || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Falha ao carregar contratos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contracts;
    return contracts.filter((c) => (c.contract || "").toLowerCase().includes(q));
  }, [contracts, search]);

  // Level 3 — employees of the picked posto
  if (selectedPosto) {
    return (
      <PostoEmployeesList
        posto={selectedPosto}
        onBack={() => setSelectedPosto(null)}
      />
    );
  }

  // Level 2 — postos of the picked contract
  if (selectedContract) {
    return (
      <ContractPostosList
        contract={selectedContract}
        onPickPosto={setSelectedPosto}
        onBack={() => setSelectedContract(null)}
      />
    );
  }

  // Level 1 — contracts grid
  return (
    <div className="space-y-4" data-testid="contratos-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-slate-700" />
            Contratos
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {contracts.length} contratos ativos · Clique para ver os postos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`${API}/admin/reports/vigilantes-sem-dpf.pdf`, "_blank")}
            data-testid="vigilantes-sem-dpf-pdf-btn"
            className="text-rose-700 border-rose-200 hover:bg-rose-50"
            title="Baixar relatório PDF com vigilantes sem DPF cadastrado"
          >
            <FileText className="w-4 h-4 mr-1" />
            Vigilantes sem DPF (PDF)
          </Button>
          <Button variant="outline" size="sm" onClick={onBack} data-testid="contratos-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Voltar
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          placeholder="Buscar contrato..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 pr-8 h-9"
          data-testid="contratos-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="py-8 text-center text-sm text-red-600">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500">
          Nenhum contrato encontrado.
        </div>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
          data-testid="contratos-grid"
        >
          {filtered.map((c) => {
            const { Icon, tint } = pickContractStyle(c.contract);
            const tw = TINT_CLASSES[tint] || TINT_CLASSES.slate;
            const photoPct = c.employees_count > 0
              ? Math.round(100 * c.with_photo / c.employees_count) : 0;
            return (
              <motion.button
                key={c.contract}
                type="button"
                onClick={() => setSelectedContract(c)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className={`bg-white border border-slate-200 rounded-xl p-4 text-left transition-all hover:shadow-lg ring-2 ring-transparent ${tw.ring} overflow-hidden relative`}
                data-testid={`contract-card-${c.contract}`}
              >
                <div className={`absolute top-0 left-0 right-0 h-1 ${tw.accent}`} />
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-11 h-11 rounded-xl ${tw.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-6 h-6 ${tw.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900 leading-tight truncate">
                      {c.contract}
                    </p>
                    <p className="text-[10px] text-slate-500">
                      {c.postos_count} posto(s)
                    </p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1 text-slate-700 font-semibold">
                      <Users className="w-3 h-3" />
                      {c.employees_count}
                    </span>
                    <span className="text-[10px] text-slate-500">
                      funcionários
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-[10px] text-slate-500 mb-0.5">
                      <span className="inline-flex items-center gap-0.5">
                        <Camera className="w-3 h-3 text-emerald-600" />
                        Fotos {c.with_photo}
                      </span>
                      <span className="font-mono">{photoPct}%</span>
                    </div>
                    <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500"
                        style={{ width: `${photoPct}%` }}
                      />
                    </div>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * ContractPostosList — Level 2. Shows postos of one contract as tiles.
 */
function ContractPostosList({ contract, onPickPosto, onBack }) {
  const [search, setSearch] = useState("");
  const { Icon, tint } = pickContractStyle(contract.contract);
  const tw = TINT_CLASSES[tint] || TINT_CLASSES.slate;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contract.postos || [];
    return (contract.postos || []).filter((p) => (p.posto || "").toLowerCase().includes(q));
  }, [contract.postos, search]);

  return (
    <div className="space-y-4" data-testid="contract-postos-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-12 h-12 rounded-xl ${tw.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-6 h-6 ${tw.text}`} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Contrato</p>
            <h3 className="text-lg font-bold text-slate-900 truncate">{contract.contract}</h3>
            <p className="text-xs text-slate-500">
              {contract.postos_count} posto(s) · {contract.employees_count} funcionário(s)
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onBack} data-testid="contract-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Contratos
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          placeholder="Buscar posto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 pr-8 h-9"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500">
          Nenhum posto encontrado.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {filtered.map((p) => (
            <motion.button
              key={p.posto}
              type="button"
              onClick={() => onPickPosto(p.posto)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className={`bg-white border border-slate-200 rounded-xl p-3 text-left transition-all hover:shadow-md ring-2 ring-transparent ${tw.ring}`}
              data-testid={`contract-posto-${p.posto}`}
            >
              <div className="flex items-start gap-2 mb-2">
                <div className={`w-9 h-9 rounded-lg ${tw.bg} flex items-center justify-center flex-shrink-0`}>
                  <MapPin className={`w-4 h-4 ${tw.text}`} />
                </div>
                <p className="text-[11px] font-semibold text-slate-900 leading-tight line-clamp-2 break-words">
                  {p.posto}
                </p>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-800">
                  <Users className="w-3 h-3" />
                  {p.count}
                </span>
                <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                  <span className="inline-flex items-center gap-0.5">
                    <Camera className="w-3 h-3 text-emerald-600" />
                    {p.with_photo}
                  </span>
                  <span className="inline-flex items-center gap-0.5">
                    <PenLine className="w-3 h-3 text-sky-600" />
                    {p.with_signature}
                  </span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * PostoEmployeesList — Level 3. Employees of a specific posto.
 */
function PostoEmployeesList({ posto, onBack }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const now = new Date();
        const params = new URLSearchParams({
          page: "1",
          per_page: "200",
          posto,
          access_year: String(now.getFullYear()),
          access_month: String(now.getMonth() + 1),
          sort_field: "nome",
          sort_order: "asc",
        });
        const r = await axios.get(`${API}/admin/employees?${params}`, { timeout: 30000 });
        if (!cancelled) setEmployees(r.data?.employees || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Falha ao carregar funcionários");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [posto]);

  return (
    <div className="space-y-4" data-testid="contract-posto-employees-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-[11px] text-slate-500 uppercase tracking-wide">Posto</p>
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 truncate">
            {posto}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {employees.length} funcionário(s)
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Postos do contrato
        </Button>
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="py-8 text-center text-sm text-red-600">{error}</div>
      ) : employees.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500">
          Nenhum funcionário neste posto.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {employees.map((e) => (
            <Card key={e.id} className="border-slate-200 hover:border-slate-300 transition-colors">
              <CardContent className="p-3 flex items-center gap-3">
                {e.photo_3x4_base64 ? (
                  <img
                    src={e.photo_3x4_base64}
                    alt={e.nome}
                    className="w-11 h-14 object-cover rounded border border-slate-200 shadow-sm bg-white flex-shrink-0"
                  />
                ) : (
                  <div className="w-11 h-14 rounded border border-slate-200 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-[11px] font-bold text-slate-500 uppercase flex-shrink-0">
                    {(e.nome || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0] || "").join("")}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate" title={e.nome}>
                    {e.nome}
                  </p>
                  <p className="text-xs font-mono text-blue-600">{e.matricula}</p>
                  <p className="text-[10px] font-mono text-slate-400 truncate">{e.cpf_masked}</p>
                </div>
                <div className="flex flex-col items-end gap-1 text-[10px]">
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                      e.has_photo_3x4
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-slate-50 text-slate-400 border border-slate-200"
                    }`}
                    title="Foto 3x4"
                  >
                    <Camera className="w-3 h-3" />
                    {e.has_photo_3x4 ? "✓" : "—"}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                      e.has_signature
                        ? "bg-sky-50 text-sky-700 border border-sky-200"
                        : "bg-slate-50 text-slate-400 border border-slate-200"
                    }`}
                    title="Rúbrica"
                  >
                    <PenLine className="w-3 h-3" />
                    {e.has_signature ? "✓" : "—"}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
